import { useEffect, useMemo, useState } from 'react'
import { asociarCursosSocio, obtenerCursosActivosParaAsociar } from '../../services/socioCursosService'
import EstadoCursoBadge from './EstadoCursoBadge'
import { Icon } from '../ui/Icons'

export default function AsociarCursosModal({ organizacionId, socioId, associatedIds, onClose, onSaved }) {
  const [courses, setCourses] = useState([])
  const [selected, setSelected] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    obtenerCursosActivosParaAsociar(organizacionId)
      .then((result) => { if (active) setCourses(result) })
      .catch((loadError) => { if (active) setError(loadError.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [organizacionId])

  useEffect(() => {
    function closeOnEscape(event) {
      if (event.key === 'Escape' && !saving) onClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [onClose, saving])

  const visibleCourses = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('es-CL')
    if (!term) return courses
    return courses.filter((course) => [course.nombre, course.nivel, course.seccion, course.anio].filter(Boolean).join(' ').toLocaleLowerCase('es-CL').includes(term))
  }, [courses, search])

  function toggle(courseId) {
    if (associatedIds.includes(courseId)) return
    setSelected((current) => current.includes(courseId) ? current.filter((id) => id !== courseId) : [...current, courseId])
  }

  async function save() {
    if (!selected.length || saving) return
    setSaving(true)
    setError('')
    try {
      await asociarCursosSocio(organizacionId, socioId, selected)
      await onSaved(selected.length)
    } catch (saveError) {
      setError(saveError.message)
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-6" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose() }}>
      <section role="dialog" aria-modal="true" aria-labelledby="associate-courses-title" className="flex max-h-[92dvh] w-full max-w-2xl flex-col rounded-t-3xl bg-surface shadow-2xl sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3 border-b border-border py-4 pl-5 pr-3 sm:p-6">
          <div><h2 id="associate-courses-title" className="text-xl font-bold">Asociar cursos</h2><p className="mt-1 text-sm text-text-secondary">Selecciona uno o varios cursos activos.</p></div>
          <button type="button" onClick={onClose} disabled={saving} className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-text-secondary hover:bg-background hover:text-primary disabled:opacity-50" aria-label="Cerrar"><Icon name="close" /></button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
          {error && <div className="mb-4 rounded-xl border border-danger/25 bg-danger/5 p-4 text-sm text-danger" role="alert">{error}</div>}
          <label className="relative block">
            <span className="sr-only">Buscar cursos disponibles</span>
            <Icon name="search" className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-text-secondary" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por nombre, nivel, sección o año" className="w-full rounded-xl border border-border bg-background py-3 pl-12 pr-4 outline-none transition focus:border-secondary focus:ring-4 focus:ring-secondary/15" />
          </label>

          {loading ? <div className="py-12 text-center text-sm text-text-secondary">Cargando cursos…</div> : visibleCourses.length === 0 ? <div className="py-12 text-center"><p className="font-medium">No hay cursos activos disponibles</p><p className="mt-1 text-sm text-text-secondary">Crea o activa un curso para poder asociarlo.</p></div> : (
            <div className="mt-4 space-y-2">
              {visibleCourses.map((course) => {
                const associated = associatedIds.includes(course.id)
                const checked = associated || selected.includes(course.id)
                return <label key={course.id} className={`flex items-start gap-3 rounded-xl border p-4 transition ${associated ? 'cursor-not-allowed border-border bg-background opacity-70' : checked ? 'cursor-pointer border-secondary bg-secondary/5' : 'cursor-pointer border-border hover:border-secondary'}`}><input type="checkbox" checked={checked} disabled={associated || saving} onChange={() => toggle(course.id)} className="mt-1 h-4 w-4 accent-primary" /><span className="min-w-0 flex-1"><span className="flex flex-wrap items-center gap-2"><span className="font-semibold">{course.nombre}</span>{associated && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">Asociado</span>}</span><span className="mt-1 block text-sm text-text-secondary">{[course.nivel, course.seccion, course.anio].filter(Boolean).join(' · ')}</span></span><EstadoCursoBadge activo={course.activo} /></label>
              })}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-border px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:flex-row sm:justify-end sm:p-6">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-border px-5 py-3 font-semibold text-text-primary hover:border-secondary disabled:opacity-50">Cancelar</button>
          <button type="button" onClick={save} disabled={!selected.length || saving} className="rounded-xl bg-primary px-5 py-3 font-semibold text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50">{saving ? 'Guardando…' : `Asociar ${selected.length || ''} curso${selected.length === 1 ? '' : 's'}`}</button>
        </div>
      </section>
    </div>
  )
}
